import { memo, useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { Box, Checkbox, FieldSet, Label, LabeledControl, Legend, Toast, useToast } from '@zillow/constellation-10';

import {
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasAttachedGarageParking,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasCoveredParkingParking,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasDetachedGarageParking,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasMultiLevelGarageParking,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasNoneParking,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasParkingLotParking,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasUndergroundGarageParking,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';

import { useGetListingDetails } from '../../../../hooks/queries/properties';
import { useBatchMutationToast } from '../../../../hooks/useBatchMutationToast';
import { usePropertyEditMutation } from '../../../../hooks/usePropertyEditMutation';
import { type AmenityFlagSave } from '../../utils';
import { useFeedAmenityMutation } from '../../../../hooks/useFeedAmenityMutation';
import { useIsInputFormDisabled } from '../../../../hooks/useIsInputFormDisabled';
import { FeedOverrideSection } from '@zg-rentals/ts-rental-manager-api-client';

import { DEFAULT_ERROR_MESSAGE } from '../../../../constants/ErrorMessageConstants';
import {
  PARKING_TYPE_METADATA,
  PARKING_TYPE_TYPE_KEYS,
  type ParkingTypeKey,
} from '../../../../constants/KeyFeaturesConstants';

import { useAuth } from '../../../AuthProvider';

type LocalState = Record<ParkingTypeKey, boolean>;

const NONE_KEY: ParkingTypeKey = 'hasNoneParking';

const ParkingTypeAmenities = (): JSX.Element | null => {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueToast } = useToast();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const parkingTypes = listingDetails?.buildingAttributes?.parkingTypes;
  const canUpdateListing = user?.features?.canUpdateListing ?? false;

  const [localState, setLocalState] = useState<LocalState>({
    hasAttachedGarage: parkingTypes?.hasAttachedGarage ?? false,
    hasCoveredParking: parkingTypes?.hasCoveredParking ?? false,
    hasDetachedGarage: parkingTypes?.hasDetachedGarage ?? false,
    hasParkingLot: parkingTypes?.hasParkingLot ?? false,
    hasMultiLevelGarage: parkingTypes?.hasMultiLevelGarage ?? false,
    hasUndergroundGarage: parkingTypes?.hasUndergroundGarage ?? false,
    hasNoneParking: parkingTypes?.hasNoneParking ?? false,
  });

  // Re-seed from listing details when they change (e.g. after a feed override is turned off and the reimport
  // restores the feed values), so the checkboxes don't keep showing the removed overrides.
  useEffect(() => {
    setLocalState({
      hasAttachedGarage: parkingTypes?.hasAttachedGarage ?? false,
      hasCoveredParking: parkingTypes?.hasCoveredParking ?? false,
      hasDetachedGarage: parkingTypes?.hasDetachedGarage ?? false,
      hasParkingLot: parkingTypes?.hasParkingLot ?? false,
      hasMultiLevelGarage: parkingTypes?.hasMultiLevelGarage ?? false,
      hasUndergroundGarage: parkingTypes?.hasUndergroundGarage ?? false,
      hasNoneParking: parkingTypes?.hasNoneParking ?? false,
    });
  }, [parkingTypes]);

  const { startRequest, handleSuccess, handleError } = useBatchMutationToast({
    successMessage: 'Parking types updated',
  });

  const isInputFormDisabled = useIsInputFormDisabled(listingDetails, canUpdateListing, FeedOverrideSection.POLICIES);
  const { isFeedListing, setFeedAmenity } = useFeedAmenityMutation();

  const saveAmenity = (key: keyof typeof PARKING_TYPE_METADATA, checked: boolean) =>
    isFeedListing
      ? setFeedAmenity(PARKING_TYPE_METADATA[key].apiKey, checked)
      : mutationMap[key]({ postData: { [PARKING_TYPE_METADATA[key].apiKey]: checked, listingAlias } });

  const { mutateAsync: updateAttachedGarage } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasAttachedGarageParking,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasAttachedGarageParking',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateCoveredParking } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasCoveredParkingParking,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasCoveredParkingParking',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateDetachedGarage } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasDetachedGarageParking,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasDetachedGarageParking',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateParkingLot } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasParkingLotParking,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasParkingLotParking',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateMultiLevelGarage } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasMultiLevelGarageParking,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasMultiLevelGarageParking',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateUndergroundGarage } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasUndergroundGarageParking,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasUndergroundGarageParking',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateNoneParking } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasNoneParking,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasNoneParking',
        details: { listingAlias },
      },
    },
  });

  const mutationMap = {
    hasAttachedGarage: updateAttachedGarage,
    hasCoveredParking: updateCoveredParking,
    hasDetachedGarage: updateDetachedGarage,
    hasParkingLot: updateParkingLot,
    hasMultiLevelGarage: updateMultiLevelGarage,
    hasUndergroundGarage: updateUndergroundGarage,
    hasNoneParking: updateNoneParking,
  } as unknown as Record<keyof LocalState, AmenityFlagSave>;

  const handleCheckboxChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fieldName = event.target.name as ParkingTypeKey;
    const checked = event.target.value === 'true';

    startRequest();

    // Special case: clicking "None" should deselect all others
    if (fieldName === NONE_KEY && checked) {
      const previousState = { ...localState };
      const newState = PARKING_TYPE_TYPE_KEYS.reduce(
        (acc, key) => ({ ...acc, [key]: key === NONE_KEY }),
        {} as Record<ParkingTypeKey, boolean>,
      );
      setLocalState(newState);

      try {
        // Set "None" to true
        await saveAmenity(NONE_KEY, true);

        // Set all others to false
        await Promise.all(PARKING_TYPE_TYPE_KEYS.filter((k) => k !== NONE_KEY).map((key) => saveAmenity(key, false)));

        handleSuccess();
      } catch (err) {
        handleError();
        setLocalState(previousState);
        const errorMessage = DatarouterError.getDisplayMessage(err, DEFAULT_ERROR_MESSAGE);
        enqueueToast(<Toast tone="critical">{errorMessage}</Toast>);
      }
      return;
    }

    // Special case: clicking any other option should deselect "None"
    const previousValue = !checked;
    const previousNoneValue = localState.hasNoneParking;
    setLocalState((prev) => ({
      ...prev,
      [fieldName]: checked,
      hasNoneParking: false, // Always deselect "None" when selecting anything else
    }));

    try {
      // Update the clicked field
      await saveAmenity(fieldName, checked);

      // If "None" was previously selected, also update it to false
      if (previousNoneValue) {
        await saveAmenity(NONE_KEY, false);
      }

      handleSuccess();
    } catch (err) {
      handleError();
      setLocalState((prev) => ({ ...prev, [fieldName]: previousValue, hasNoneParking: previousNoneValue }));
      const errorMessage = DatarouterError.getDisplayMessage(err, DEFAULT_ERROR_MESSAGE);
      enqueueToast(<Toast tone="critical">{errorMessage}</Toast>);
    }
  };

  return (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: '400', flex: { lg: '1 1 50%', base: 'auto' } }}>
      <FieldSet legend={<Legend>Parking options</Legend>} disabled={isInputFormDisabled}>
        {PARKING_TYPE_TYPE_KEYS.map((fieldName) => {
          const metadata = PARKING_TYPE_METADATA[fieldName];
          return (
            <LabeledControl
              key={`parkingTypes-${metadata.label}`}
              label={<Label>{metadata.label}</Label>}
              control={
                <Checkbox
                  name={fieldName}
                  value={String(!localState[fieldName])}
                  checked={localState[fieldName]}
                  onChange={handleCheckboxChange}
                />
              }
            />
          );
        })}
      </FieldSet>
    </Box>
  );
};

export default memo(ParkingTypeAmenities);
